import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Personal Loan Assistance",
  "Business Loan Assistance",
  "Home Loan Guidance",
  "Loan Against Property",
  "Other Services",
];

const AMOUNTS = [
  "Below ₹1 Lakh",
  "₹1 – 5 Lakh",
  "₹5 – 25 Lakh",
  "₹25 Lakh – 1 Crore",
  "Above ₹1 Crore",
];

const EMPTY = { full_name: "", phone: "", email: "", loan_category: "", desired_amount: "", message: "" };

export const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry submitted successfully", {
        description: "Thank you. Our team will reach out to you shortly.",
      });
      setForm(EMPTY);
    } catch (err) {
      toast.error("Submission failed", {
        description: "Please check your details and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="loan-enquiry-form" onSubmit={submit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="enquiry-name" className="block text-xs text-[#A1A1AA] mb-2 tracking-wide">Full Name *</label>
          <input
            id="enquiry-name"
            data-testid="loan-enquiry-name-input"
            className="input-gold"
            required
            minLength={2}
            placeholder="Your full name"
            value={form.full_name}
            onChange={set("full_name")}
          />
        </div>
        <div>
          <label htmlFor="enquiry-phone" className="block text-xs text-[#A1A1AA] mb-2 tracking-wide">Phone Number *</label>
          <input
            id="enquiry-phone"
            data-testid="loan-enquiry-phone-input"
            className="input-gold"
            required
            minLength={8}
            type="tel"
            placeholder="+91 XXXXX XXXXX"
            value={form.phone}
            onChange={set("phone")}
          />
        </div>
      </div>

      <div>
        <label htmlFor="enquiry-email" className="block text-xs text-[#A1A1AA] mb-2 tracking-wide">Email Address *</label>
        <input
          id="enquiry-email"
          data-testid="loan-enquiry-email-input"
          className="input-gold"
          required
          type="email"
          placeholder="you@example.com"
          value={form.email}
          onChange={set("email")}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="enquiry-category" className="block text-xs text-[#A1A1AA] mb-2 tracking-wide">Loan Category *</label>
          <select
            id="enquiry-category"
            data-testid="loan-enquiry-category-select"
            className="input-gold appearance-none"
            required
            value={form.loan_category}
            onChange={set("loan_category")}
          >
            <option value="" disabled>Select a category</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="enquiry-amount" className="block text-xs text-[#A1A1AA] mb-2 tracking-wide">Desired Amount</label>
          <select
            id="enquiry-amount"
            data-testid="loan-enquiry-amount-select"
            className="input-gold appearance-none"
            value={form.desired_amount}
            onChange={set("desired_amount")}
          >
            <option value="">Select a range (optional)</option>
            {AMOUNTS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="enquiry-message" className="block text-xs text-[#A1A1AA] mb-2 tracking-wide">Message</label>
        <textarea
          id="enquiry-message"
          data-testid="loan-enquiry-message-input"
          className="input-gold min-h-[110px] resize-y"
          placeholder="Tell us briefly about your requirement"
          value={form.message}
          onChange={set("message")}
        />
      </div>

      <p className="text-[11px] text-[#71717A] leading-relaxed">
        By submitting this form, you consent to be contacted by CREDNOVA SERVICES PRIVATE LIMITED regarding your enquiry. Loan approval is subject to the eligibility criteria and policies of the respective bank or financial institution.
      </p>

      <button
        type="submit"
        data-testid="loan-enquiry-submit-button"
        disabled={loading}
        className="btn-gold w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm disabled:opacity-60"
      >
        <span className="relative z-10 flex items-center gap-2">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {loading ? "Submitting..." : "Submit Enquiry"}
        </span>
      </button>
    </form>
  );
};
